import {AfterViewInit, Component, ElementRef, OnInit, Renderer2, ViewChild} from '@angular/core';
import {MatPaginator} from "@angular/material/paginator";
import {MatSort} from "@angular/material/sort";
import {UtilsService} from "../../shared/utils.service";
import {AppService} from "../../app.service";
import {ApiRequestService} from "../../shared/api-request.service";
import {MatDialog} from "@angular/material/dialog";
import {AssetsService} from "../../assets/assets.service";
import {tap} from "rxjs/operators";
import {AssetModel} from "../../models/asset.model";
import {AssetsEditComponent} from "../../assets/assets-edit/assets-edit.component";
import {AssetsViewComponent} from "../../assets/assets-view/assets-view.component";
import {AssetsFilterComponent} from "../../assets/assets-filter/assets-filter.component";
import {saveAs} from "file-saver";
import moment from "moment/moment";
import {UserPublicProfileComponent} from "../../shared/user-public-profile/user-public-profile.component";
import {ApiDataSource} from "../../utils/api-data-source";
import {ActivatedRoute} from "@angular/router";
import {AssetsFilters} from "../../assets/assets-filter/assets-filter.interface";

@Component({
  selector: 'app-sites-assets',
  templateUrl: './sites-assets.component.html',
  styleUrls: ['./sites-assets.component.scss']
})
export class SitesAssetsComponent implements OnInit, AfterViewInit {

  parent_id: number;
  child_id: number;
  site_id: number;
  path: string;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;
  dataSource = new SiteAssetsDataSource(this.app, this.api);

  displayedColumns = [
    'select',
    'id',
    'name',
    'code',
    'asset_type',
    'flags',
    'status',
    'owner_type',
    'maintenance_due_date',
    'next_inspection_due_date',
    'condition_rating',
    'buttons'
  ];

  @ViewChild('fileInput') fileInput: ElementRef;

  constructor(
    public utils: UtilsService,
    public app: AppService,
    private api: ApiRequestService,
    public route: ActivatedRoute,
    private dialog: MatDialog,
    public renderer: Renderer2,
    public assetsService: AssetsService
  ) { }

  ngOnInit() {
    // Get the site id from the route params.
    this.parent_id = Number(this.route.parent.snapshot.params['parent_id']);
    this.child_id = Number(this.route.parent.snapshot.params['child_id']);

    if ( this.child_id || (typeof this.route.parent.snapshot.params['child_id'] == 'undefined' && this.parent_id) ) {
      // Store the site id.
      this.site_id = this.child_id ? this.child_id : this.parent_id;

      // get sites path edit/view
      this.path = this.route.parent.snapshot.routeConfig.path;

      this.dataSource.site_id = this.site_id;

      // get the data
      this.dataSource.getData(true);
    }
  }

  ngAfterViewInit() {
    // Reset the paginator when sorting
    this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));

    const _tap = tap(() => {
      this.dataSource.limit = this.paginator.pageSize;
      this.dataSource.offset = this.paginator.pageIndex;
      this.dataSource.order = this.sort.direction;

      // sorting for utc time by parsing original time
      if (this.sort.active === "date_created_UTC") {
        this.dataSource.order_by = "date_created";
      } else {
        this.dataSource.order_by = this.sort.active;
      }


      this.dataSource.getData(false);
    });

    // Subscribe to the paginator tap events
    this.paginator.page.pipe(_tap).subscribe();
    this.sort.sortChange.pipe(_tap).subscribe();
  }

  /**
   * Create a new Asset.
   *
   * @return {void}
   */
  onAdd(): void {
    this.onEdit(new AssetModel());
  }

  /**
   * Create new or update existing Asset.
   *
   * @param {AssetModel} asset The asset to be edited.
   * @return {void}
   */
  onEdit(asset: AssetModel): void {
    this.dialog
      .open(AssetsEditComponent, {
        width: '90%',
        data: {
          asset_id: asset.id,
          site_id: this.dataSource.site_id
        }
      })
      .afterClosed()
      .subscribe(() => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      });
  }

  /**
   * Display the Asset data.
   *
   * @param {number} id - The unique identifier of the asset.
   * @return {void}
   */
  onView(id: number): void {
    this.utils.showComponentDialog(
      AssetsViewComponent,
      {
        asset_id: id
      },
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        this.dataSource.getData(true);
      }
    );
  }

  /**
   * Shows a modal to confirm archiving selected Assets and archives them.
   *
   * @return {void}
   */
  onRemoveSelected(): void {
    this.utils.showModal(
      'Archive Asset(s)',
      `Are you sure you want to archive ${this.dataSource.selection.selected.length} asset(s)?`,
      () => {
        this.remove(this.dataSource.selection.selected.join(','), 'Asset(s) have been archived.');
      }
    );
  }

  /**
   * Archives the specified Asset.
   *
   * @param {AssetModel} asset - The asset to be archived.
   * @return {void}
   */
  onRemove(asset: AssetModel): void {
    this.utils.showModal(
      'Archive Asset',
      `Are you sure you want to archive "${asset.name}"?`,
      () => {
        this.remove(asset.id, 'Asset has been archived.');
      }
    );
  }

  /**
   * Archives Assets with the specified IDs.
   *
   * @param {any} ids - IDs of assets to be removed.
   * @param msg
   * @return {void}
   */
  private remove(ids: any, msg: string): void {
    this.api.makeRequest('delete', `v2/assets/${ids}`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData(false);
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Restore Asset.
   *
   * @param {number} id - The ID of the asset to be restored.
   * @return {void}
   */
  onRestore(id: number): void {
    this.utils.showModal(
      'Restore Asset',
      'Are you sure you want to restore this asset?',
      () => {
        this.restore(id, 'The Asset was restored.');
      }
    );
  }

  /**
   * Restore selected Assets.
   *
   * @return {void}
   */
  onRestoreSelected(): void {
    this.utils.showModal(
      'Restore Selected Asset(s)',
      'Are you sure you want to restore the selected assets?',
      () => {
        this.restore(this.dataSource.selection.selected.join(','), 'The selected Asset(s) were restored.');
      }
    );
  }

  /**
   * Restores Assets with the specified IDs.
   *
   * @param {any} ids - IDs of assets to be restored.
   * @param msg
   * @return {void}
   */
  private restore(ids: any, msg: string): void {
    this.api.makeRequest('put', `v2/assets/${ids}/restore`)
      .then((response) => {
        this.utils.showToast(msg);
        this.dataSource.selection.clear();
        this.dataSource.getData();
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Sets the tags for the filters and updates the asset filter preferences.
   * Retrieves the data from the API using the updated filters.
   *
   * @param {number[]} $event
   * @return {void}
   */
  getOutputTagsFilter($event: number[]): void {
    this.dataSource.tag_ids = $event;
    this.dataSource.getData(true);
  }

  /**
   * Show component dialog for filtering Assets.
   *
   * @return {void}
   */
  onFilter(): void {
    this.utils.showComponentDialog(AssetsFilterComponent, {
        archived: this.dataSource.filters.archived,
        asset_type_ids: this.dataSource.filters.asset_type_ids,
        contractor_owner_ids: this.dataSource.filters.contractor_owner_ids,
        vendor_ids: this.dataSource.filters.vendor_ids,
        contractor_ids: this.dataSource.filters.contractor_ids,
        user_ids: this.dataSource.filters.user_ids,
        status: this.dataSource.filters.status,
        owner_type: this.dataSource.filters.owner_type,
        condition_rating: this.dataSource.filters.condition_rating,
        is_visible: this.dataSource.filters.is_visible,
        is_mobile: this.dataSource.filters.is_mobile,
        is_safety_critical: this.dataSource.filters.is_safety_critical,
        date_type: this.dataSource.filters.date_type,
        date_range: this.dataSource.filters.date_range,
        hide_sites: this.dataSource.filters.hide_sites
      }, {
        width: '1200px'
      },
      (results: any): void => {
        // Terminate early if it is undefined or an empty string.
        if ( typeof results === 'undefined' || results === '' ) {
          return;
        }

        // Apply the filters.
        this.dataSource.filters.archived = results.archived ?? this.dataSource.filters.archived;
        this.dataSource.filters.asset_type_ids = results.asset_type_ids ?? this.dataSource.filters.asset_type_ids;
        this.dataSource.filters.contractor_owner_ids = results.contractor_owner_ids ?? this.dataSource.filters.contractor_owner_ids;
        this.dataSource.filters.vendor_ids = results.vendor_ids ?? this.dataSource.filters.vendor_ids;
        this.dataSource.filters.contractor_ids = results.contractor_ids ?? this.dataSource.filters.contractor_ids;
        this.dataSource.filters.user_ids = results.user_ids ?? this.dataSource.filters.user_ids;
        this.dataSource.filters.status = results.status ?? this.dataSource.filters.status;
        this.dataSource.filters.owner_type = results.owner_type ?? this.dataSource.filters.owner_type;
        this.dataSource.filters.condition_rating = results.condition_rating ?? this.dataSource.filters.condition_rating;
        this.dataSource.filters.is_visible = results.is_visible ?? this.dataSource.filters.is_visible;
        this.dataSource.filters.is_mobile = results.is_mobile ?? this.dataSource.filters.is_mobile;
        this.dataSource.filters.is_safety_critical = results.is_safety_critical ?? this.dataSource.filters.is_safety_critical;
        this.dataSource.filters.date_type = results.date_type ?? this.dataSource.filters.date_type;
        this.dataSource.filters.date_range = results.date_range ?? this.dataSource.filters.date_range;
        this.dataSource.getData();
      }
    );
  }

  /**
   * Exports the list of Assets into the specified format and sends a download request to the browser.
   *
   * @param type csv, xls, xlsx, pdf.
   */
  onExportSelected(type: string = 'csv'):void {
    this.dataSource.makeDownloadRequest(`v2/assets/export/${type}` + (this.dataSource.selection.selected.length ? '/' + this.dataSource.selection.selected.join(',') : ''), {
      site_id: this.dataSource.site_id,
      archived: this.dataSource.filters.archived,
      asset_type_ids: this.dataSource.filters.asset_type_ids.join(','),
      contractor_owner_ids: this.dataSource.filters.contractor_owner_ids.join(','),
      vendor_ids: this.dataSource.filters.vendor_ids.join(','),
      contractor_ids: this.dataSource.filters.contractor_ids.join(','),
      user_ids: this.dataSource.filters.user_ids.join(',') ,
      status: this.dataSource.filters.status ?? 'all',
      owner_type: this.dataSource.filters.owner_type ?? 'all',
      condition_rating: this.dataSource.filters.condition_rating ?? 'all',
      date_type: this.dataSource.filters.date_type,
      date_range: this.dataSource.filters.date_range.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(','),
      tag_ids: this.dataSource.tag_ids.join(',')
    })
      .then((response) => {
        if(type == 'xlsx') {
          this.utils.showToast(response.message || 'Your export is queued in the background. You will receive an email when it is ready.');
        } else {
          saveAs(response, `Assets - ${moment().toString()}.${type}`);
        }
      })
      .catch((errorResponse) => {
        this.utils.handleAPIErrors(errorResponse);
      });
  }

  /**
   * Open the User's public profile dialog.
   *
   * @param hash
   */
  onUserPublicView(hash: string):void {
    this.utils.showComponentDialog(
      UserPublicProfileComponent,
      hash,
      { width: '90%' },
      () => {
        // Refresh the list regardless of how the dialog is closed.
        // this.dataSource.getData();
      }
    );
  }

}

export class SiteAssetsDataSource extends ApiDataSource {
  order_by = 'id';
  order = 'asc';

  site_id = 0;
  tag_ids: number[] = [];

  filters: AssetsFilters = {
    archived: 'false',
    asset_type_ids: [],
    contractor_owner_ids: [],
    vendor_ids: [],
    contractor_ids: [],
    user_ids: [],
    status: 'all',
    owner_type: 'all',
    condition_rating: 'all',
    is_visible: 'all',
    is_mobile: 'all',
    is_safety_critical: 'all',
    date_type: 'maintenance_due_date',
    date_range: [],
    hide_sites: true
  };

  getData(resetOffset: boolean = false) {
    this.makeRequest('v2/assets', resetOffset, {
      site_id: this.site_id,
      archived: this.filters.archived,
      asset_type_ids: this.filters.asset_type_ids.join(','),
      contractor_owner_ids: this.filters.contractor_owner_ids.join(','),
      vendor_ids: this.filters.vendor_ids.join(','),
      contractor_ids: this.filters.contractor_ids.join(','),
      user_ids: this.filters.user_ids.join(',') ,
      status: this.filters.status ?? 'all',
      owner_type: this.filters.owner_type ?? 'all',
      condition_rating: this.filters.condition_rating ?? 'all',
      is_visible: this.filters.is_visible ?? 'all',
      is_mobile: this.filters.is_mobile ?? 'all',
      is_safety_critical: this.filters.is_safety_critical ?? 'all',
      date_type: this.filters.date_type,
      date_range: this.filters.date_range.map((date: Date) => {
        return date.getTime() / 1000;
      }).join(','),
      tag_ids: this.tag_ids.join(',')
    });
  }
}
